import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, readoutStyle, FONT, px } from '../theme';
import { createListRow, createPanel } from '../ui/panel';
import { createButton } from '../ui/button';
import { layoutOf, watchResize } from '../ui/layout';
import type { VoyageChoice, VoyageEvent } from '@core/voyage/events';
import type { CollectedClue } from '@core/mystery/ledger';
import type { WorldMapScene } from './WorldMapScene';
import type { Campaign } from '@core/campaign/campaign';
import { maybeShowTutorial } from '../ui/adviserPanel';

/**
 * Ein Ereignis unterwegs.
 *
 * Laeuft als eigene Szene ueber der pausierten Karte. Solange hier etwas
 * ansteht, faehrt das Schiff nicht weiter — die Entscheidung soll ueberlegt
 * getroffen werden, nicht unter Reaktionsdruck.
 *
 * Die Tafel waechst mit ihrem Inhalt. Frueher war sie fest 300 Pixel hoch und
 * die Wahlmoeglichkeiten standen alle 50 Pixel — bei vergroesserter Schrift
 * liefen sie ineinander, und auf einem Telefon ragte der Text unten heraus.
 */
export class VoyageEventScene extends Phaser.Scene {
  private event!: VoyageEvent;
  private chosen: VoyageChoice | null = null;
  /** Was die Wahl tatsaechlich in die Akte gebracht hat. */
  private gained: readonly CollectedClue[] = [];

  constructor() {
    super('VoyageEvent');
  }

  init(data: { event: VoyageEvent }): void {
    this.event = data.event;
    this.chosen = null;
    this.gained = [];
  }

  private get campaign(): Campaign | undefined {
    return this.registry.get('campaign') as Campaign | undefined;
  }

  /**
   * Verweigert die Mannschaft diese Wahl?
   *
   * Bei sehr schlechter Moral geht sie keine Umwege mehr, die nur der Akte
   * dienen — nicht, solange es eine andere Moeglichkeit gibt. Das ist die
   * Rechnung fuer die Toten, die man fuer Hinweise in Kauf genommen hat.
   */
  private refused(choice: VoyageChoice): boolean {
    const campaign = this.campaign;
    if (campaign === undefined || !campaign.toll.refusesDetours) return false;
    if (!choice.yieldsEvidence) return false;
    return this.event.choices.some((other) => other !== choice && !other.yieldsEvidence);
  }

  create(): void {
    this.redraw();
    watchResize(this, () => this.redraw());

    // Tastatur: Ziffern waehlen, Leertaste oder Enter fahren weiter.
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.chosen === null) {
        const index = Number(event.key) - 1;
        const choice = this.event.choices[index];
        if (choice !== undefined && !this.refused(choice)) this.choose(choice);
      } else if (event.key === ' ' || event.key === 'Enter') {
        this.continueVoyage();
      }
    });

    // Der richtige Moment fuer den Hinweis auf die Kosten unterwegs: wenn
    // gerade eine Entscheidung ansteht, die welche verursacht.
    const campaign = this.registry.get('campaign') as Campaign | undefined;
    if (campaign !== undefined) maybeShowTutorial(this, campaign, 'voyage');
  }

  private choose(choice: VoyageChoice): void {
    this.chosen = choice;
    // Sofort anwenden, damit der Ausgang sagen kann, was wirklich passiert ist.
    const worldMap = this.scene.get('WorldMap') as unknown as WorldMapScene;
    this.gained = worldMap.applyEventChoice(choice);
    this.redraw();
  }

  private continueVoyage(): void {
    const worldMap = this.scene.get('WorldMap') as unknown as WorldMapScene;
    this.scene.stop();
    worldMap.resumeAfterEvent();
  }

  private redraw(): void {
    this.children.removeAll(true);

    const layout = layoutOf(this);
    const panelWidth = Math.min(620, layout.width - layout.margin * 2);
    const inner = panelWidth - 48;
    const left = layout.centreX - panelWidth / 2;

    // Die Karte darunter abdunkeln, damit der Blick hier landet.
    this.add.rectangle(0, 0, layout.width, layout.height, PALETTE.abyss, 0.82).setOrigin(0, 0);
    const panel = createPanel(this, left, 0, panelWidth, 10);

    // Alles wird zuerst relativ zur Oberkante gesetzt; die Tafel wird danach
    // vertikal zentriert.
    const parts: Phaser.GameObjects.GameObject[] = [];
    let y = 22;

    const kind = this.add.text(left + 24, y, t(`voyage.kind.${this.event.kind}`), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: TEXT_COLOUR.coast,
    });
    parts.push(kind);
    y += kind.height + 10;

    const body = this.add.text(left + 24, y, t(this.event.textKey), {
      ...readoutStyle(13),
      wordWrap: { width: inner },
      lineSpacing: 4,
    });
    parts.push(body);
    y += body.height + 12;

    // Bei Bemerkungen der Mannschaft schwingt mit, wie es an Bord steht.
    const campaign = this.campaign;
    if (this.event.kind === 'crew_remark' && campaign !== undefined) {
      const mood = campaign.toll.moodKey.replace('toll.mood.', '');
      const remark = this.add.text(left + 24, y, t(`toll.remark.${mood}`), {
        ...readoutStyle(12),
        color: PALETTE.inkDim,
        fontStyle: 'italic',
        wordWrap: { width: inner },
      });
      parts.push(remark);
      y += remark.height + 12;
    }
    y += 8;

    if (this.chosen === null) {
      this.event.choices.forEach((choice, index) => {
        const refused = this.refused(choice);
        // Die Kosten, wie sie bei der jetzigen Moral tatsaechlich anfallen.
        const shown = campaign?.adjustChoice(choice) ?? choice;
        const row = createListRow(this, left + 24, y, {
          width: inner,
          height: 44,
          title: `${index + 1}  ${t(choice.labelKey)}`,
          detail: refused ? t('voyage.refused') : describeCost(shown),
          ...(refused ? {} : { onClick: () => this.choose(choice) }),
        });
        if (refused) row.setAlpha(0.5);
        parts.push(row);
        y += row.height + 8;
      });
      y += 12;
    } else {
      y = this.renderOutcome(parts, left, y, inner);
      const button = createButton(
        this,
        left + panelWidth / 2,
        y + 22,
        t('voyage.continue'),
        () => this.continueVoyage(),
        { width: 200, height: 44, fontSize: 12 },
      );
      parts.push(button);
      y += 44 + 22;
    }

    const panelHeight = y;
    panel.height = panelHeight;
    panel.setSize(panelWidth, panelHeight);

    // Zentrieren — und nie ueber den oberen Rand hinaus, auch wenn das Fenster
    // flacher ist als die Tafel.
    const top = Math.max(8, (layout.height - panelHeight) / 2);
    panel.y = top;
    for (const part of parts) {
      (part as unknown as { y: number }).y += top;
    }
  }

  private renderOutcome(
    parts: Phaser.GameObjects.GameObject[],
    left: number,
    y: number,
    inner: number,
  ): number {
    const choice = this.chosen as VoyageChoice;

    const outcome = this.add.text(left + 24, y, t(choice.outcomeKey), {
      ...readoutStyle(12),
      color: PALETTE.inkDim,
      wordWrap: { width: inner },
      lineSpacing: 3,
    });
    parts.push(outcome);
    y += outcome.height + 12;

    // Was die Wahl konkret bewirkt hat — untereinander, damit es sich nicht
    // gegenseitig verdeckt.
    const note = (text: string, colour: string): void => {
      const line = this.add.text(left + 24, y, text, {
        ...readoutStyle(11),
        color: colour,
        fontStyle: 'italic',
        wordWrap: { width: inner },
      });
      parts.push(line);
      y += line.height + 4;
    };

    if (choice.soulsRescued > 0) {
      note(t('voyage.souls.rescued', { count: choice.soulsRescued }), TEXT_COLOUR.base);
    }
    /*
     * Nur melden, was in der Akte gelandet ist.
     *
     * Frueher stand hier bei jedem Fund „Hinweis gesichert“ — auch wenn der
     * laufende Akt gar keine Anomalie mehr brauchte und der Fund verfiel.
     */
    if (choice.yieldsEvidence) {
      note(
        t(this.gained.length > 0 ? 'voyage.evidence.gained' : 'voyage.evidence.redundant'),
        this.gained.length > 0 ? TEXT_COLOUR.landmark : PALETTE.inkFaint,
      );
    }
    const charged = this.campaign?.adjustChoice(choice) ?? choice;
    if (charged.hullDamage > 0.5) {
      note(t('voyage.hull.damaged', { value: Math.round(charged.hullDamage) }), TEXT_COLOUR.danger);
    }

    return y + 12;
  }
}

/** Kosten einer Wahl in Worten — die Grundlage der Abwägung. */
function describeCost(choice: VoyageChoice): string {
  const parts: string[] = [];
  if (choice.delayHours > 0) {
    parts.push(t('voyage.cost.hours', { hours: choice.delayHours.toFixed(1) }));
  }
  if (choice.fuelCost > 0) {
    parts.push(t('voyage.cost.fuel', { fuel: Math.round(choice.fuelCost) }));
  }
  // Ein negativer Betrag ist Nachschub. Er gehoert genauso deutlich hin wie
  // die Kosten — beim Bunkerhalt ist er der ganze Punkt.
  if (choice.fuelCost < 0) {
    parts.push(t('voyage.gain.fuel', { fuel: Math.round(-choice.fuelCost) }));
  }
  if (choice.hullDamage > 0.5) {
    parts.push(t('voyage.cost.hull', { value: Math.round(choice.hullDamage) }));
  }
  // Was eine Wahl einbringt, gehoert neben das, was sie kostet — sonst ist es
  // keine Abwaegung, sondern eine Warnung.
  if (choice.soulsRescued > 0) {
    parts.push(t('voyage.gain.souls', { count: choice.soulsRescued }));
  }
  return parts.length === 0 ? t('voyage.cost.none') : parts.join('  ·  ');
}
